import { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Hourglass, ChevronRight, Loader2 } from "lucide-react";

/**
 * Tuile "Commissions à venir" affichée sur la page Commissions.
 * Liste les commandes terminées dont le solde n'a PAS été réglé — engagement futur
 * envers les vendeurs (utile pour anticiper la trésorerie de paie).
 */
export const UpcomingCommissionsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getUpcomingCommissions();
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData({ total_estimated: 0, total_orders: 0, sellers: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="kpi-card border-amber-200">
        <CardContent className="pt-6 flex items-center justify-center h-24">
          <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
        </CardContent>
      </Card>
    );
  }

  const total = data?.total_estimated || 0;
  const count = data?.total_orders || 0;
  const sellers = data?.sellers || [];

  return (
    <>
      <Card
        className={`kpi-card border-amber-200 ${count > 0 ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
        data-testid="upcoming-commissions-card"
        onClick={() => count > 0 && setOpen(true)}
        title={count > 0 ? "Cliquez pour voir le détail" : ""}
      >
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center shrink-0">
              <Hourglass className="h-6 w-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                Commissions à venir
                {count > 0 && (
                  <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px] py-0">
                    {count} cmd
                  </Badge>
                )}
              </p>
              <p className="text-2xl font-heading font-bold text-amber-700 currency">
                {formatCurrency(total)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {count > 0
                  ? "Commandes terminées en attente du règlement final"
                  : "Aucun engagement en attente"}
              </p>
            </div>
            {count > 0 && <ChevronRight className="h-4 w-4 text-amber-600 shrink-0" />}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="upcoming-commissions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hourglass className="h-5 w-5 text-amber-600" />
              Commissions à venir — détail
            </DialogTitle>
            <DialogDescription>
              Liste des commandes terminées dont le solde client n'est pas encore réglé.
              La commission sera due dès réception du paiement final.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {sellers.map((s) => (
              <div key={s.seller_id} className="rounded-lg border bg-white">
                <div className="flex items-center justify-between p-3 bg-amber-50/60 border-b">
                  <div>
                    <div className="font-medium">{s.seller_name}</div>
                    <div className="text-xs text-muted-foreground">{s.orders_count} commande(s) en attente</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-muted-foreground">Estimation</div>
                    <div className="font-mono text-base font-semibold text-amber-700">
                      {formatCurrency(s.estimated_commission)}
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Référence</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Terminée le</TableHead>
                      <TableHead>Acompte</TableHead>
                      <TableHead className="text-right">HT</TableHead>
                      <TableHead className="text-right">Estimation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {s.orders.map((o) => (
                      <TableRow key={o.order_id} data-testid={`upcoming-row-${o.order_id}`}>
                        <TableCell className="text-xs font-mono">{o.reference || "—"}</TableCell>
                        <TableCell className="text-sm">{o.client_name}</TableCell>
                        <TableCell className="text-xs">{o.completed_at ? formatDate(o.completed_at) : "—"}</TableCell>
                        <TableCell>
                          {o.deposit_paid ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">Versé</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Non versé</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{formatCurrency(o.amount_ht)}</TableCell>
                        <TableCell className="text-right font-mono text-amber-700 font-medium">
                          {formatCurrency(o.estimated_commission)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
            {sellers.length === 0 && (
              <p className="text-sm text-center text-muted-foreground py-6">
                Aucune commande terminée en attente de règlement final.
              </p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpcomingCommissionsCard;
